import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../axiosInstance';
import VerticalPrimeTable from '../../common/DataTable/verticalPrimeTable';
import LogsVerticalPrimeTable from '../../common/DataTable/logsVerticalPrimeTable';
import Tag from 'apollo-react/components/Tag';
import CircularProgress from 'apollo-react/components/CircularProgress';
import useFileDownload from '../../../hooks/api/files/useFileDownload';

export default function LogDetails({ persona }) {
  const { t, ready } = useTranslation();
  const [log, setLog] = useState();
  const [actor, setActor] = useState();
  const [target, setTarget] = useState();
  const [fieldChangesData, setfieldChangesData] = useState([]);
  const { id } = useParams();
  const { downloadFile } = useFileDownload();

  const renderFileLinks = useCallback(
    (files) =>
      files.map((file) => (
        <span
          key={file.id}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            downloadFile(file.id, file.name);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') downloadFile(file.id, file.name);
          }}
          style={{
            color: '#0557d5',
            cursor: 'pointer',
            textDecoration: 'underline',
            marginRight: '8px',
          }}
        >
          {file.name}
        </span>
      )),
    [downloadFile]
  );

  const renderFieldValue = useCallback(
    (value) => {
      if (Array.isArray(value)) {
        return renderFileLinks(value);
      }
      if (typeof value === 'string' && value.includes(',')) {
        return value
          .split(',')
          .map((val) => (
            <Tag
              className="log-tag"
              label={val.trim()}
              variant="neutral"
              key={val.trim()}
            />
          ));
      }
      return value ?? t('not_available');
    },
    [renderFileLinks, t]
  );

  useEffect(() => {
    const fetchLog = async () => {
      await axiosInstance
        .get(`/api/logs/log/${id}`)
        .then((response) => {
          setLog(response.data.log);
          const parsedChanges = JSON.parse(
            response.data.log?.field_changes || '{}'
          );
          setfieldChangesData([
            {
              Field: t('field_changes'),
              Old_Value: t('old_value'),
              New_Value: t('new_value'),
            },
            ...Object.entries(parsedChanges).map(([field, changes]) => ({
              Field: field,
              Old_Value: renderFieldValue(changes?.old),
              New_Value: renderFieldValue(changes?.new),
            })),
          ]);
          setActor(response.data.actor);
          setTarget(response.data.target);
        })
        .catch((err) => {
          console.error('Error fetching log details:', err);
        });
    };
    fetchLog();
  }, [id, persona, t, renderFieldValue]);

  if (!ready) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="card">
        {/* <div className="card-header bg-primary text-white">Log Details</div> */}
        {log && (
          <div className="card-body">
            <div>
              <VerticalPrimeTable
                data={{
                  [t('id')]: log.id,
                  [t('action')]: log.action,
                  [t('actor_name')]: actor?.username,
                  [t('actor_email')]: actor?.email,
                  [t('created_at')]: log.createdAt,
                  [t('entity_id')]: log.entity_id,
                  [t('name')]: log.entity_name,
                  [t('resource')]: log.resource,
                  ...(target
                    ? {
                        [t('target_name')]: target?.username,
                        [t('target_email')]: target?.email,
                      }
                    : {}),

                  [t('updated_at')]: log.updatedAt,
                  [t('version')]: log.version,
                }}
                actions={[]}
              />
            </div>

            {fieldChangesData.length > 1 && (
              <div>
                <LogsVerticalPrimeTable
                  data={fieldChangesData}
                  columns={[
                    {
                      field: 'Field',
                      header: t('field_name'),
                    },
                    {
                      field: 'Old_Value',
                      header: t('old_value'),
                    },
                    {
                      field: 'New_Value',
                      header: t('new_value'),
                    },
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
